import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { requestQueue } from './RequestQueue';
import { requestCache } from './RequestCache';
import { detectLanguage } from '../utils/languageDetection';
import { log, logError } from '../utils/logging';
import { playbookParagraphPlainText } from '../utils/playbookRowText';
import * as tokenStorage from '../utils/tokenStorage';
import { shouldAllowTokenRefresh, clearSessionActivity } from '../utils/sessionActivity';
import { getCurrentFirebaseUser, extractFirebaseUserInfo } from './firebaseAuth';
import {
  CostLimitExceededError,
  notifyUsageLimitFromFetchResponse,
  notifyUsageLimitFromResponsePayload,
} from '../utils/usageLimitError';

/** Grep-friendly prefix for Word add-in login / post-login 401 investigations. */
const AUTH_TRACE = '[Courtyard][AuthTrace]';

// API Response wrapper type
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  status_code?: number;
}

// Project and SubProject types
export interface Project {
  id: string;
  name: string;
  description?: string;
  domain?: string;
  sub_domain?: string;
}

export interface SubProject {
  id: string;
  name: string;
  project_id: string;
  description?: string;
}

export interface Clause {
  id: string;
  clause_number?: string;
  client_request?: string;
  bank_of_changes?: string;
  documentType?: string;
  submission_status?: {
    schedule_submitted?: boolean;
    response_submitted?: boolean;
  };
  lock_status?: {
    locked?: boolean;
    locked_by?: string;
  };
}

/** The two backend route trees a sub-project can live under. */
export type ApiDomain = 'construction-loan' | 'real-estate';

class ApiService {
  private axiosInstance: AxiosInstance;
  private static readonly LONG_REQUEST_TIMEOUT_MS = 120 * 60 * 1000; // 120 minutes
  private baseURL: string;
  private useRequestQueue: boolean = true;
  private useCache: boolean = true;
  private activeProjectId?: string;
  /** Single-flight guard so concurrent 401s trigger only one Firebase token refresh. */
  private refreshPromise: Promise<string | null> | null = null;
  /**
   * Learned domain per sub-project. A sub-project belongs to exactly one route tree
   * (its parent project's sub_domain). Once we know it — from an explicit UI hint or a
   * successful call — we stop probing the other domain, which eliminates the wasteful
   * construction-loan 400s on Master Cross Check projects and halves request volume.
   */
  private subProjectDomainCache = new Map<string, ApiDomain>();

  /** Record the domain a sub-project resolved to, so later calls skip the probe. */
  private rememberSubProjectDomain(subProjectId: string | undefined, domain: ApiDomain): void {
    if (subProjectId) this.subProjectDomainCache.set(subProjectId, domain);
  }

  /** Domain learned for a sub-project, if any. */
  getCachedSubProjectDomain(subProjectId: string): ApiDomain | undefined {
    return this.subProjectDomainCache.get(subProjectId);
  }

  /**
   * Decide which domain(s) to call for a sub-project request.
   *
   * - If we have a definitive domain (cached from a prior success, or an explicit UI
   *   hint such as Master Cross Check => real-estate), call ONLY that domain. This is
   *   the fix for the double-probe: no more construction-loan 400 noise, and a genuine
   *   failure surfaces its real error instead of being masked by fallback probing.
   * - Otherwise (unknown origin: session restore, filename inference, opening an
   *   existing sub-project) fall back to probing both, preferring construction-loan
   *   first for backward compatibility.
   */
  private resolveDomains(
    subProjectId: string | undefined,
    hint?: ApiDomain,
  ): { domains: ApiDomain[]; definitive: boolean } {
    const cached = subProjectId ? this.subProjectDomainCache.get(subProjectId) : undefined;
    const primary = cached ?? hint;
    if (primary) return { domains: [primary], definitive: true };
    return { domains: ['construction-loan', 'real-estate'], definitive: false };
  }

  /**
   * Run a sub-project request against the resolved domain(s).
   *
   * Calls `request(domain)` for each candidate domain. On the first `success` response
   * it caches the domain and returns the payload. When the domain is definitive we make
   * a single call and surface its real error; only in non-definitive (probe) mode do we
   * swallow per-domain errors and try the next, throwing `notFoundMessage` if all fail.
   */
  private async requestWithDomain(
    subProjectId: string | undefined,
    hint: ApiDomain | undefined,
    notFoundMessage: string,
    request: (domain: ApiDomain) => Promise<AxiosResponse<any>>,
  ): Promise<ApiResponse<any>> {
    const { domains, definitive } = this.resolveDomains(subProjectId, hint);
    let firstMeaningfulError: any = null;

    for (const domain of domains) {
      try {
        const response = await request(domain);
        if (response.data?.success) {
          this.rememberSubProjectDomain(subProjectId, domain);
          return response.data;
        }
        // 200 with success=false. In definitive mode surface it; otherwise keep probing.
        if (definitive) {
          const data = response.data as { message?: string; detail?: string } | undefined;
          const msg = data?.detail || data?.message;
          throw new Error(typeof msg === 'string' && msg.trim() ? msg : notFoundMessage);
        }
      } catch (err: any) {
        // Definitive domain: do not mask the real cause behind fallback probing.
        if (definitive) {
          throw this.formatAxiosError(err, notFoundMessage);
        }
        // Probe mode: remember the first useful (non-404/400) error so we can surface a
        // real cause rather than a generic "endpoint not found" if every probe fails.
        const status = err?.response?.status;
        if (!firstMeaningfulError && status !== 404 && status !== 400) {
          firstMeaningfulError = err;
        }
        continue;
      }
    }

    if (firstMeaningfulError) throw this.formatAxiosError(firstMeaningfulError, notFoundMessage);
    throw new Error(notFoundMessage);
  }

  setActiveProjectId(projectId: string | undefined): void {
    this.activeProjectId = projectId?.trim() || undefined;
  }

  constructor() {
    // Get API URL from environment or default to relative path (proxied through webpack dev server)
    // Note: process.env is replaced by webpack DefinePlugin at build time
    const envApiUrl = (process.env as any)?.API_BACKEND_BASE_URL;
    
    // In development, use relative URL to leverage webpack proxy (avoids mixed content issues)
    // In production, use the full backend URL
    let baseUrl: string;
    // Check if envApiUrl is set and not the string "undefined" (webpack DefinePlugin quirk)
    if (envApiUrl && envApiUrl !== 'undefined' && envApiUrl !== 'null') {
      baseUrl = envApiUrl;
    } else {
      // Use relative URL in development (webpack proxy handles HTTPS->HTTP)
      // This avoids mixed content issues when frontend is HTTPS and backend is HTTP
      baseUrl = '/api/v1';
    }
    
    // Ensure /api/v1 prefix is included if using absolute URL
    if (baseUrl.startsWith('http') && !baseUrl.includes('/api/v1')) {
      baseUrl = baseUrl.endsWith('/') 
        ? `${baseUrl}api/v1` 
        : `${baseUrl}/api/v1`;
    }
    
    this.baseURL = baseUrl;

    if (typeof window !== 'undefined') {
      tokenStorage.initializeTokenStorage();
    }
    
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /** Backend iap_auth treats Bearer tokens as Firebase ID tokens only when iss is securetoken.google.com. */
  private isFirebaseIdToken(token: string): boolean {
    if (!token || token.split('.').length !== 3) return false;
    try {
      const part = token.split('.')[1];
      const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
      const iss = String(payload.iss || '');
      return iss.startsWith('https://securetoken.google.com/');
    } catch {
      return false;
    }
  }

  /** Prefer dashboard-style auth_token_{environment}, then legacy keys. */
  private getResolvedAuthToken(): { token: string | null; tokenKey: string | null } {
    if (typeof window === 'undefined') return { token: null, tokenKey: null };
    try {
      const t = tokenStorage.getToken();
      if (t) return { token: t, tokenKey: `auth_token_${tokenStorage.getEnvironment()}` };
    } catch {
      /* localStorage unavailable */
    }
    const legacyKeys = ['authToken', 'token', 'auth_token_development', 'auth_token_production', 'auth_token'];
    for (const key of legacyKeys) {
      try {
        const t = localStorage.getItem(key);
        if (t) return { token: t, tokenKey: key };
      } catch {
        /* ignore */
      }
    }
    return { token: null, tokenKey: null };
  }

  /** For `fetch()` streams that bypass axios interceptors. */
  private applyFetchAuthHeaders(headers: Record<string, string>): void {
    const token = this.getResolvedAuthToken().token;
    if (!token) return;
    headers['Authorization'] = `Bearer ${token}`;
    if (this.isFirebaseIdToken(token)) {
      headers['X-Firebase-ID-Token'] = token;
    }
  }

  private setupInterceptors() {
    // Request interceptor - add auth token if available
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const { token, tokenKey } = this.getResolvedAuthToken();

        const reqPath = `${config.baseURL ?? ''}${config.url ?? ''}`;
        const isAuthRoute = /\/auth\/firebase-callback\b/.test(reqPath);
        
        // Never overwrite the explicit Firebase ID token the callback route sets itself.
        // During a silent refresh the stored backend token is stale, and clobbering the
        // fresh ID token here would make the re-exchange fail.
        if (token && config.headers && !isAuthRoute) {
          config.headers.Authorization = `Bearer ${token}`;
          if (this.isFirebaseIdToken(token)) {
            (config.headers as Record<string, string>)['X-Firebase-ID-Token'] = token;
          }
        }

        const authAttached = !!(token && config.headers && config.headers.Authorization);
        console.log(
          `${AUTH_TRACE} → ${(config.method || 'GET').toUpperCase()} ${reqPath} | authAttached=${authAttached} | key=${tokenKey ?? '—'} | firebaseShape=${token ? this.isFirebaseIdToken(token) : false} | authRoute=${isAuthRoute}`
        );
        if (!token && !isAuthRoute) {
          console.warn(`${AUTH_TRACE} no token in localStorage for protected request. keys=`, this.getAvailableLocalStorageKeys());
        }
        
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          const cfg = error.config as (InternalAxiosRequestConfig & { _authRetry?: boolean }) | undefined;
          const reqUrl = cfg ? `${cfg.baseURL ?? ''}${cfg.url ?? ''}` : '(unknown url)';
          const isAuthRoute = /\/auth\/firebase-callback\b/.test(reqUrl);
          const sentAuth = !!(cfg?.headers && (cfg.headers as any).Authorization);
          const hadTokenBefore = !!this.getCurrentAuthToken();
          const detail = error.response?.data;
          const wwwAuth = error.response?.headers && (error.response.headers['www-authenticate'] || (error.response.headers as any)['WWW-Authenticate']);
          console.warn(`${AUTH_TRACE} ← 401 ${reqUrl}`, {
            sentAuthorizationHeader: sentAuth,
            hadTokenInStorageBeforeHandler: hadTokenBefore,
            responseDetail: detail,
            wwwAuthenticate: wwwAuth ?? null,
          });

          // Attempt one silent Firebase token refresh + retry before giving up.
          // This prevents the "first 401 nukes the token → infinite tokenless
          // poll storm" failure mode seen in the 2026-06-22 arnon-tl incident.
          // Skip the auto-retry for multipart uploads: the FormData body and its
          // boundary marker can desync on re-stream, silently corrupting the file.
          const isMultipart = cfg?.data instanceof FormData;
          if (cfg && !cfg._authRetry && !isAuthRoute && hadTokenBefore && !isMultipart) {
            cfg._authRetry = true;
            const refreshed = await this.refreshAuthToken();
            if (refreshed) {
              console.warn(`${AUTH_TRACE} token refreshed after 401; retrying ${reqUrl}`);
              return this.axiosInstance(cfg);
            }
          }
          if (isMultipart) {
            console.warn(`${AUTH_TRACE} skipping auto-retry for multipart upload on 401; caller must re-authenticate`);
          }

          // Refresh impossible (no Firebase user) or genuinely failed → clear token,
          // signal re-auth, and tag the error so polling callers can stop instead of
          // hammering the API with tokenless requests.
          // Skip clearing when the failing request is the /auth/firebase-callback
          // itself (i.e. the inner refresh attempt): the outer interceptor invocation
          // will clear the token, preventing a double auth:token-expired event.
          if (!isAuthRoute) {
            if (this.getCurrentAuthToken()) {
              console.warn(`${AUTH_TRACE} clearing stored token after 401 (refresh unavailable/failed)`);
              this.clearAuthToken();
            } else {
              console.warn(`${AUTH_TRACE} 401 but no token in storage (login failed or race).`);
            }
          }
          (error as AxiosError & { isAuthError?: boolean }).isAuthError = true;
        } else if (error.response?.status === 429 || error.response?.status === 400) {
          const usageLimit = notifyUsageLimitFromResponsePayload(error.response.data);
          if (usageLimit) {
            (error as AxiosError & { isCostLimitError?: boolean }).isCostLimitError = true;
          } else if (error.response?.status !== 429) {
            console.error('API Error:', error);
          }
        } else {
          // Log other errors
          console.error('API Error:', error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Silently refresh the backend access token via Firebase.
   *
   * Forces a fresh Firebase ID token from the signed-in user and re-exchanges it
   * for a new backend access token. Single-flight: concurrent 401s share one
   * refresh. Returns the new token, or null when refresh is impossible (e.g. no
   * Firebase currentUser because login happened in a dialog / system browser) or
   * genuinely fails — in which case the caller clears the token and prompts re-login.
   */
  private async refreshAuthToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async (): Promise<string | null> => {
      try {
        // Idle / absolute-cap gate: a background poll (which is what drives most
        // 401-triggered refreshes in the pane) must NOT silently keep a dead
        // session alive. After 60m idle or the 12h cap, clear the token so the
        // login overlay shows instead of refreshing.
        if (!shouldAllowTokenRefresh()) {
          console.warn(`${AUTH_TRACE} refresh blocked: session idle/expired — clearing token`);
          this.clearAuthToken();
          return null;
        }
        const user = getCurrentFirebaseUser();
        if (!user) {
          console.warn(`${AUTH_TRACE} cannot refresh token: no Firebase currentUser (dialog/system-browser login)`);
          return null;
        }
        const idToken = await user.getIdToken(true);
        if (!idToken) {
          console.warn(`${AUTH_TRACE} cannot refresh token: getIdToken returned empty`);
          return null;
        }
        const info = extractFirebaseUserInfo(user);
        const newToken = await this.firebaseLogin(
          idToken,
          info.email,
          info.name,
          info.uid,
          info.emailVerified,
          'refresh',
          'firebase',
        );
        return newToken ?? null;
      } catch (e) {
        console.warn(`${AUTH_TRACE} token refresh failed`, e);
        return null;
      }
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Get available localStorage keys for debugging
   */
  private getAvailableLocalStorageKeys(): string[] {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (e) {
      return [];
    }
  }

  /** Surface FastAPI `detail` / ApiResponse `message` from failed axios calls. */
  private formatAxiosError(error: unknown, fallback: string): Error {
    if (axios.isAxiosError(error)) {
      const usageLimit = notifyUsageLimitFromResponsePayload(error.response?.data);
      if (usageLimit) {
        return new CostLimitExceededError(usageLimit);
      }

      const data = error.response?.data as {
        message?: string;
        detail?: string | Array<{ msg?: string } | string>;
      } | undefined;
      const detail = data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return new Error(detail);
      }
      if (Array.isArray(detail) && detail.length > 0) {
        return new Error(
          detail
            .map((d) => (typeof d === 'object' && d && 'msg' in d ? String(d.msg) : String(d)))
            .join('; '),
        );
      }
      if (typeof data?.message === 'string' && data.message.trim()) {
        return new Error(data.message);
      }
      if (error.message?.trim()) {
        return new Error(error.message);
      }
    }
    if (error instanceof Error && error.message.trim()) {
      return error;
    }
    return new Error(fallback);
  }

  /**
   * Manually set authentication token
   */
  /**
   * Persist token the same way as the web dashboard (auth_token_development / auth_token_production).
   */
  setAuthToken(token: string): void {
    try {
      tokenStorage.setToken(token);
      const readBack = tokenStorage.getToken();
      console.log(`${AUTH_TRACE} setAuthToken env=${tokenStorage.getEnvironment()} readBackMatch=${readBack === token}`);
    } catch (e) {
      console.error(`${AUTH_TRACE} Failed to save auth token:`, e);
    }
  }

  /**
   * Get current auth token (for debugging)
   */
  getCurrentAuthToken(): string | null {
    return this.getResolvedAuthToken().token;
  }

  /**
   * Login with Firebase authentication
   * Exchanges Firebase ID token for backend access token
   */
  async firebaseLogin(
    idToken: string,
    userEmail: string,
    userName: string,
    userUid: string,
    emailVerified: boolean = false,
    provider: string = 'password',
    authMethod: string = 'firebase'
  ): Promise<string | null> {
    try {
      console.log('[API] Starting Firebase login with backend...');
      console.log('[API] User email:', userEmail);
      console.log('[API] Provider:', provider);

      const response = await this.axiosInstance.post('/auth/firebase-callback', {
        id_token: idToken,
        user_email: userEmail,
        user_name: userName,
        user_uid: userUid,
        email_verified: emailVerified,
        provider: provider,
        auth_method: authMethod,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-Firebase-ID-Token': idToken,
        },
      });

      console.log(`${AUTH_TRACE} /auth/firebase-callback response`, {
        status: response.status,
        success: response.data?.success,
        hasNestedAccessToken: !!(response.data?.data?.access_token),
        tokenType: response.data?.data?.token_type,
      });

      if (response.data.success && response.data.data?.access_token) {
        const token = response.data.data.access_token;
        this.setAuthToken(token);
        console.log(`${AUTH_TRACE} Firebase exchange OK`);
        return token;
      }

      throw new Error('No access token in Firebase callback response');
    } catch (error: any) {
      console.error(`${AUTH_TRACE} Firebase login error`, {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  }

  /**
   * Clear authentication token
   */
  clearAuthToken(): void {
    tokenStorage.clearAuthData();
    // Reset idle / absolute-session tracking so the next login starts fresh.
    clearSessionActivity();
    const tokenKeys = [
      'authToken',
      'token',
      'auth_token_development',
      'auth_token_production',
      'auth_token',
    ];
    for (const key of tokenKeys) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Continue to next key
      }
    }
    
    // Dispatch event to notify components that authentication has been cleared
    window.dispatchEvent(new CustomEvent('auth:token-expired', {
      detail: { reason: 'token_expired' }
    }));
    
    console.warn(`${AUTH_TRACE} Auth token cleared (expired/invalid/logout)`);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getCurrentAuthToken() !== null;
  }

  /**
   * Get all projects
   * Tries both general projects endpoint and domain-specific endpoints
   * Uses caching and request queue for optimization
   */
  async getProjects(): Promise<ApiResponse<Project[]>> {
    const cacheKey = 'projects';
    
    // Check cache first
    if (this.useCache) {
      const cached = requestCache.get<ApiResponse<Project[]>>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      // Use request queue if enabled
      const executeRequest = async () => {
        // Try general projects endpoint first
        try {
          const response = await this.axiosInstance.get('/projects/');
          if (response.data.success) {
            // Cache the result
            if (this.useCache) {
              requestCache.set(cacheKey, response.data, 2 * 60 * 1000); // 2 minutes
            }
            return response.data;
          }
        } catch (err) {
          // If general endpoint fails, try domain-specific endpoints
          console.log('General /projects/ endpoint failed, trying domain-specific endpoints');
        }

        // Try real-estate projects
        try {
          const response = await this.axiosInstance.get('/real-estate/projects');
          if (response.data.success) {
            // Cache the result
            if (this.useCache) {
              requestCache.set(cacheKey, response.data, 2 * 60 * 1000); // 2 minutes
            }
            return response.data;
          }
        } catch (err) {
          // Continue to next endpoint
        }

        throw new Error('No projects endpoint available');
      };

      // Use request queue if enabled
      if (this.useRequestQueue) {
        return await requestQueue.enqueue(executeRequest, 'normal');
      } else {
        return await executeRequest();
      }
    } catch (error: any) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  /**
   * Get sub-projects for a project
   * Uses caching and request queue for optimization
   */
  async getSubProjects(
    projectId: string,
    domainHint?: 'construction-loan' | 'real-estate',
  ): Promise<ApiResponse<SubProject[]>> {
    const cacheKey = `sub-projects-${projectId}`;
    
    // Check cache first
    if (this.useCache) {
      const cached = requestCache.get<ApiResponse<SubProject[]>>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const executeRequest = async () => {
        const endpoints =
          domainHint === 'construction-loan'
            ? [
                `/construction-loan/projects/${projectId}/sub-projects`,
                `/real-estate/projects/${projectId}/sub-projects`,
              ]
            : domainHint === 'real-estate'
              ? [
                  `/real-estate/projects/${projectId}/sub-projects`,
                  `/construction-loan/projects/${projectId}/sub-projects`,
                ]
              : [
                  `/construction-loan/projects/${projectId}/sub-projects`,
                  `/real-estate/projects/${projectId}/sub-projects`,
                ];

        for (const endpoint of endpoints) {
          try {
            const response = await this.axiosInstance.get(endpoint);
            if (response.data.success) {
              // Cache the result
              if (this.useCache) {
                requestCache.set(cacheKey, response.data, 2 * 60 * 1000); // 2 minutes
              }
              return response.data;
            }
          } catch (err) {
            // Try next endpoint
            continue;
          }
        }

        throw new Error('No sub-projects endpoint found');
      };

      // Use request queue if enabled
      if (this.useRequestQueue) {
        return await requestQueue.enqueue(executeRequest, 'normal');
      } else {
        return await executeRequest();
      }
    } catch (error: any) {
      console.error('Error fetching sub-projects:', error);
      throw error;
    }
  }

  /**
   * Get sub-project details including clauses
   */
  async getSubProjectDetails(
    subProjectId: string,
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<any>> {
    try {
      // Prefer the cached/known domain; only probe both when the origin is unknown.
      const { domains, definitive } = this.resolveDomains(subProjectId, domainHint);

      let authError: unknown = null;
      for (const domain of domains) {
        let response: AxiosResponse<any>;
        try {
          response = await this.axiosInstance.get(`/${domain}/sub-projects/${subProjectId}`);
        } catch (err) {
          // A 401 fails identically on every domain probe — stop and surface it as an
          // auth error so polling callers can halt instead of looping forever.
          if ((err as AxiosError & { isAuthError?: boolean })?.isAuthError ||
              (err as AxiosError)?.response?.status === 401) {
            authError = err;
            break;
          }
          // Definitive domain: surface the real error rather than masking it.
          if (definitive) throw err;
          continue;
        }

        if (response.data.success) {
          this.rememberSubProjectDomain(subProjectId, domain);
          // Attach detected domain so callers can route domain-specific calls correctly.
          return { ...response.data, detected_domain: domain };
        }
        // 200 with success=false: always surface the backend's own explanation.
        // This is outside the catch block so it cannot be swallowed by probe-mode continue.
        const data = response.data as { message?: string; detail?: string } | undefined;
        const backendMsg = data?.detail || data?.message;
        throw new Error(typeof backendMsg === 'string' && backendMsg.trim() ? backendMsg : 'Sub-project not found');
      }

      if (authError) throw authError;
      throw new Error('Sub-project not found');
    } catch (error: any) {
      console.error('Error fetching sub-project details:', error);
      throw error;
    }
  }

  /**
   * Resolve a Word document filename to matching sub-project(s).
   * Returns { matches: [{ project_id, sub_project_id, project_name, sub_project_name }] }.
   */
  async getSubProjectByFilename(
    filename: string,
    projectId?: string
  ): Promise<ApiResponse<{ matches: Array<{ project_id: string; sub_project_id: string; project_name: string; sub_project_name: string }> }>> {
    try {
      let url = `/real-estate/sub-project-by-filename?filename=${encodeURIComponent(filename)}`;
      if (projectId) {
        url += `&project_id=${encodeURIComponent(projectId)}`;
      }
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error resolving sub-project by filename:', error);
      return { success: false, message: error.message || 'Failed to resolve', data: { matches: [] } };
    }
  }

  /**
   * BrainShare entries for the current user in a sub-project.
   */
  async getUserBrainShares(subProjectId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.axiosInstance.get(
        `/brainshare/sub-project/${subProjectId}/user-brainshares`,
      );
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching BrainShare entries:', error);
      throw this.formatAxiosError(error, 'Failed to load BrainShare entries');
    }
  }

  async updateBrainShareStatus(
    entryId: string,
    status: string,
    declinedReason?: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.axiosInstance.put(`/brainshare/${entryId}/status`, {
        status,
        ...(declinedReason ? { declined_reason: declinedReason } : {}),
      });
      return response.data;
    } catch (error: unknown) {
      console.error('Error updating BrainShare status:', error);
      throw this.formatAxiosError(error, 'Failed to update BrainShare entry');
    }
  }

  async deleteBrainShare(entryId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.axiosInstance.delete(`/brainshare/${entryId}`);
      return response.data;
    } catch (error: unknown) {
      console.error('Error deleting BrainShare entry:', error);
      throw this.formatAxiosError(error, 'Failed to delete BrainShare entry');
    }
  }

  /**
   * Edit a clause field (client request or bank of changes).
   */
  async editClause(
    subProjectId: string,
    params: {
      clause_id: string;
      field: 'client_request' | 'bank_of_changes';
      new_value: string;
      is_new_option?: boolean;
      share_to_brainshare?: boolean;
    },
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.axiosInstance.post(
        `/real-estate/sub-projects/${subProjectId}/edit-clause`,
        params,
      );
      return response.data;
    } catch (error: any) {
      console.error('Error editing clause:', error);
      throw error;
    }
  }

  /**
   * Update a clause
   */
  async updateClause(subProjectId: string, clauseId: string, updates: {
    client_request?: string;
    bank_of_changes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/edit-clause`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_id: clauseId,
            ...updates,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Update clause endpoint not found');
    } catch (error: any) {
      console.error('Error updating clause:', error);
      throw error;
    }
  }

  /**
   * Submit clause to schedule
   */
  async submitClauseToSchedule(subProjectId: string, clauseId: string): Promise<ApiResponse<any>> {
    try {
      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/append-to-schedule-of-changes`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_id: clauseId,
            update_submission_status: true,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Submit to schedule endpoint not found');
    } catch (error: any) {
      console.error('Error submitting clause to schedule:', error);
      throw error;
    }
  }

  /**
   * Submit clause to client response
   */
  async submitClauseToResponse(subProjectId: string, clauseId: string): Promise<ApiResponse<any>> {
    try {
      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/append-to-client-response`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_id: clauseId,
            update_submission_status: true,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Submit to response endpoint not found');
    } catch (error: any) {
      console.error('Error submitting clause to response:', error);
      throw error;
    }
  }

  /**
   * Update clause response submission status only (used by Word add-in after reverting document in Word).
   * Does not touch files or Drive; only updates cross_checks_results submission_status.
   */
  async updateClauseResponseSubmissionStatus(
    subProjectId: string,
    clauseId: string,
    responseSubmitting: boolean,
    responseRevertContext?: import('../word/trackChanges/RedlineInserter').SubmitRevertContext,
  ): Promise<ApiResponse<any>> {
    try {
      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/update-clause-response-submission-status`,
      ];

      const body: Record<string, unknown> = {
        clause_id: clauseId,
        response_submitted: responseSubmitting,
      };
      if (responseSubmitting && responseRevertContext) {
        body.response_revert_context = responseRevertContext;
      }

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, body);
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Update clause response submission status endpoint not found');
    } catch (error: any) {
      console.error('Error updating clause response submission status:', error);
      throw error;
    }
  }

  /**
   * Toggle clause verification status
   */
  async toggleClauseVerification(subProjectId: string, clauseId: string): Promise<ApiResponse<any>> {
    try {
      // First get current clause to check verification status
      const subProjectResponse = await this.getSubProjectDetails(subProjectId);
      if (!subProjectResponse.success || !subProjectResponse.data) {
        throw new Error('Failed to get sub-project details');
      }

      const clauses = subProjectResponse.data.cross_checks_results || [];
      const clause = clauses.find((c: any) => c.id === clauseId);
      if (!clause) {
        throw new Error('Clause not found');
      }

      const currentVerified = clause.verification_status?.verified || false;
      const newVerified = !currentVerified;

      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/update-clause-verification`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_id: clauseId,
            verified: newVerified,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Toggle verification endpoint not found');
    } catch (error: any) {
      console.error('Error toggling clause verification:', error);
      throw error;
    }
  }

  /**
   * Set lock status for multiple clauses in one request (batch close).
   */
  async setClausesLock(
    subProjectId: string,
    clauseIds: string[],
    locked: boolean,
  ): Promise<ApiResponse<{
    clause_ids?: string[];
    locked?: boolean;
    updated_count?: number;
    not_found?: string[];
  }>> {
    try {
      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/update-clauses-lock`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_ids: clauseIds,
            locked,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Batch set clause lock endpoint not found');
    } catch (error: any) {
      console.error('Error batch setting clause lock:', error);
      throw error;
    }
  }

  /**
   * Set clause lock status explicitly (used for bulk close).
   */
  async setClauseLock(
    subProjectId: string,
    clauseId: string,
    locked: boolean,
  ): Promise<ApiResponse<any>> {
    try {
      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/update-clause-lock`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_id: clauseId,
            locked,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Set clause lock endpoint not found');
    } catch (error: any) {
      console.error('Error setting clause lock:', error);
      throw error;
    }
  }

  /**
   * Toggle clause lock status
   */
  async toggleClauseLock(subProjectId: string, clauseId: string): Promise<ApiResponse<any>> {
    try {
      // First get current clause to check lock status
      const subProjectResponse = await this.getSubProjectDetails(subProjectId);
      if (!subProjectResponse.success || !subProjectResponse.data) {
        throw new Error('Failed to get sub-project details');
      }

      const clauses = subProjectResponse.data.cross_checks_results || [];
      const clause = clauses.find((c: any) => c.id === clauseId);
      if (!clause) {
        throw new Error('Clause not found');
      }

      const currentLocked = clause.lock_status?.locked || false;
      const newLocked = !currentLocked;

      const endpoints = [
        `/real-estate/sub-projects/${subProjectId}/update-clause-lock`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {
            clause_id: clauseId,
            locked: newLocked,
          });
          if (response.data.success) {
            return response.data;
          }
        } catch (err) {
          continue;
        }
      }

      throw new Error('Toggle lock endpoint not found');
    } catch (error: any) {
      console.error('Error toggling clause lock:', error);
      throw error;
    }
  }

  /**
   * Move a cross-check clause to another document category (MCC).
   */
  async moveClause(
    subProjectId: string,
    clauseId: string,
    targetDocumentType: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ): Promise<ApiResponse<{ updated_cross_checks_results?: unknown[]; reverted?: boolean }>> {
    const needsReanalysis =
      targetDocumentType === 'sales_agreement' || targetDocumentType === 'appendix_a';
    const endpoint = `/real-estate/sub-projects/${subProjectId}/move-clause`;

    try {
      const response = await this.axiosInstance.post(
        endpoint,
        {
          clause_id: clauseId,
          target_document_type: targetDocumentType,
        },
        { timeout: needsReanalysis ? 600_000 : undefined },
      );
      if (response.data?.success) {
        return response.data;
      }
      throw new Error(response.data?.message || 'Failed to move clause');
    } catch (error) {
      throw this.formatAxiosError(error, 'Failed to move clause');
    }
  }

  /**
   * Re-analyze a cross-check clause with user feedback (MCC).
   */
  async reanalyzeClause(
    subProjectId: string,
    clauseId: string,
    payload: {
      sales_agreement_feedback?: string;
      bank_of_changes_feedback?: string;
    },
  ): Promise<ApiResponse<{ updated_cross_checks_results?: unknown[] }>> {
    const endpoint = `/real-estate/sub-projects/${subProjectId}/reanalyze-clause`;

    try {
      const response = await this.axiosInstance.post(
        endpoint,
        {
          clause_id: clauseId,
          sales_agreement_feedback: payload.sales_agreement_feedback,
          bank_of_changes_feedback: payload.bank_of_changes_feedback,
        },
        { timeout: 600_000 },
      );
      if (response.data?.success) {
        return response.data;
      }
      throw new Error(response.data?.message || 'Failed to re-analyze clause');
    } catch (error) {
      throw this.formatAxiosError(error, 'Failed to re-analyze clause');
    }
  }

  /**
   * Construction Loan: move a clause between matter tabs (no re-analysis).
   */
  async moveClauseMatter(
    subProjectId: string,
    clauseId: string,
    targetMatter: 'commercial' | 'legal' | 'other',
  ): Promise<ApiResponse<{ updated_cross_checks_results?: unknown[] }>> {
    const body = { clause_id: clauseId, target_matter: targetMatter };
    const endpoints = [
      `/construction-loan/sub-projects/${subProjectId}/move-clause-matter`,
      `/real-estate/sub-projects/${subProjectId}/move-clause-matter`,
    ];

    let lastError: Error | null = null;
    for (const endpoint of endpoints) {
      try {
        const response = await this.axiosInstance.post(endpoint, body);
        if (response.data?.success) {
          return response.data;
        }
        lastError = new Error(response.data?.message || 'Move clause matter endpoint failed');
      } catch (err: any) {
        const status = err.response?.status;
        if (status === 404 || status === 403 || status === 400) {
          lastError = err;
          continue;
        }
        throw err;
      }
    }
    throw lastError || new Error('Move clause matter endpoint failed');
  }

  /**
   * Construction Loan: re-analyze past negotiations options for one clause.
   */
  async reanalyzePastNegotiations(
    subProjectId: string,
    clauseId: string,
    pastNegotiationsFeedback: string,
  ): Promise<ApiResponse<{ updated_cross_checks_results?: unknown[] }>> {
    const endpoint = `/construction-loan/sub-projects/${subProjectId}/reanalyze-past-negotiations`;
    const response = await this.axiosInstance.post(
      endpoint,
      {
        clause_id: clauseId,
        past_negotiations_feedback: pastNegotiationsFeedback,
      },
      { timeout: 600_000 },
    );
    if (response.data?.success) {
      return response.data;
    }
    throw new Error(response.data?.message || 'Reanalyze past negotiations endpoint failed');
  }

  /**
   * Rephrase text while preserving meaning
   * Language is automatically detected from the text
   */
  async rephraseText(
    text: string,
    context?: string,
    paragraphXml?: string  // Optional XML parameter
  ): Promise<ApiResponse<{ processed_text: string; paragraph_xml?: string }>> {
    try {
      // Auto-detect language from text
      const language = detectLanguage(text);

      // Use longer timeout for LLM operations (120 seconds)
      const payload: any = {
        text,
        language,
        context,
      };

      if (this.activeProjectId) {
        payload.project_id = this.activeProjectId;
      }
      
      // If XML is provided, include it in the request
      if (paragraphXml) {
        payload.paragraph_xml = paragraphXml;
        console.log('[api.ts] Including paragraph_xml in rephrase request:', {
          xmlLength: paragraphXml.length,
          xmlPreview: paragraphXml.substring(0, 200)
        });
      } else {
        console.warn('[api.ts] No paragraph_xml provided to rephraseText');
      }
      
      const response = await this.axiosInstance.post('/add-ins/rephrase', payload, {
        timeout: 120000, // 120 seconds for LLM processing
      });
      return response.data;
    } catch (error: any) {
      console.error('Error rephrasing text:', error);
      throw error;
    }
  }

  /**
   * Polish text to improve grammar, style, and clarity
   * Language is automatically detected from the text
   */
  async polishText(
    text: string,
    preserveMeaning: boolean = true,
    paragraphXml?: string  // Optional XML parameter
  ): Promise<ApiResponse<{ processed_text: string; paragraph_xml?: string }>> {
    try {
      // Auto-detect language from text
      const language = detectLanguage(text);

      // Use longer timeout for LLM operations (120 seconds)
      const payload: any = {
        text,
        language,
        preserve_meaning: preserveMeaning,
      };

      if (this.activeProjectId) {
        payload.project_id = this.activeProjectId;
      }
      
      // If XML is provided, include it in the request
      if (paragraphXml) {
        payload.paragraph_xml = paragraphXml;
        console.log('[api.ts] Including paragraph_xml in polish request:', {
          xmlLength: paragraphXml.length,
          xmlPreview: paragraphXml.substring(0, 200)
        });
      } else {
        console.warn('[api.ts] No paragraph_xml provided to polishText');
      }
      
      const response = await this.axiosInstance.post('/add-ins/polish', payload, {
        timeout: 120000, // 120 seconds for LLM processing
      });
      return response.data;
    } catch (error: any) {
      console.error('Error polishing text:', error);
      throw error;
    }
  }

  /**
   * Ask a question using only the project's past-negotiations playbook (Construction Loan).
   */
  async playbookAsk(
    playbookContext: string,
    question: string,
    conversation?: Array<{ role: 'user' | 'assistant'; content: string }>,
    projectLabel?: string,
    projectId?: string,
    language?: 'he' | 'en',
  ): Promise<ApiResponse<{
    answer: string;
    recommendation?: 'accept_as_is' | 'decline' | 'suggest_edit' | 'unknown';
  }>> {
    try {
      const payload: {
        playbook_context: string;
        question: string;
        conversation?: Array<{ role: string; content: string }>;
        project_label?: string;
        project_id?: string;
        language?: string;
      } = {
        playbook_context: playbookContext,
        question,
      };
      if (conversation && conversation.length > 0) {
        payload.conversation = conversation;
      }
      if (projectLabel?.trim()) {
        payload.project_label = projectLabel.trim();
      }
      if (projectId?.trim()) {
        payload.project_id = projectId.trim();
      }
      if (language) {
        payload.language = language;
      }
      const response = await this.axiosInstance.post('/add-ins/playbook-ask', payload, {
        timeout: 300000,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error in playbook ask:', error);
      throw error;
    }
  }

  async playbookAskFeedback(params: {
    projectId: string;
    feedbackText: string;
    language: 'he' | 'en';
    recommendation: 'accept_as_is' | 'decline' | 'suggest_edit' | 'unknown';
    conversationSnapshot?: Array<{ role: 'user' | 'assistant'; content: string }>;
    clauseContextSnippet?: string;
    priorFeedbackTurns?: Array<{ role: 'user' | 'assistant'; content: string }>;
    clientRequest?: string;
    askAgentExplanation?: string;
    playbookReferencesSummary?: string;
  }): Promise<ApiResponse<{
    status: 'accepted' | 'clarify' | 'rejected';
    assistant_message: string;
    new_version?: number;
    skill_summary?: string;
  }>> {
    try {
      const response = await this.axiosInstance.post('/add-ins/playbook-ask-feedback', {
        project_id: params.projectId,
        feedback_text: params.feedbackText,
        language: params.language,
        recommendation: params.recommendation,
        conversation_snapshot: params.conversationSnapshot,
        clause_context_snippet: params.clauseContextSnippet,
        prior_feedback_turns: params.priorFeedbackTurns,
        client_request: params.clientRequest,
        ask_agent_explanation: params.askAgentExplanation,
        playbook_references_summary: params.playbookReferencesSummary,
      }, {
        timeout: 300000,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error in playbook ask feedback:', error);
      throw error;
    }
  }

  /**
   * Ask a question about the full document text (Word add-in Ask mode, Gemini via backend → engine).
   */
  async documentAsk(
    documentText: string,
    question: string,
    conversation?: Array<{ role: 'user' | 'assistant'; content: string }>,
    reviewPartyContext?: string,
    attachments?: Array<{ filename: string; content_base64: string; mime_type?: string }>
  ): Promise<ApiResponse<{ answer: string }>> {
    try {
      const payload: {
        document_text: string;
        question: string;
        conversation?: Array<{ role: string; content: string }>;
        review_party_context?: string;
        attachments?: Array<{ filename: string; content_base64: string; mime_type?: string }>;
      } = {
        document_text: documentText,
        question,
      };
      if (conversation && conversation.length > 0) {
        payload.conversation = conversation;
      }
      if (reviewPartyContext?.trim()) {
        payload.review_party_context = reviewPartyContext.trim();
      }
      if (attachments && attachments.length > 0) {
        payload.attachments = attachments;
      }
      const response = await this.axiosInstance.post('/add-ins/document-ask', payload, {
        timeout: 300000,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error in document ask:', error);
      throw error;
    }
  }

  /**
   * Polish text with streaming support
   * Yields text chunks as they arrive, then final result with paragraph_xml
   * Language is automatically detected from the text
   */
  async *polishTextStream(
    text: string,
    preserveMeaning: boolean = true,
    paragraphXml?: string  // Optional XML parameter
  ): AsyncGenerator<{ type: string; content?: string; processed_text?: string; paragraph_xml?: string; error?: string }, void, unknown> {
    try {
      // Auto-detect language from text
      const language = detectLanguage(text);

      const payload: any = {
        text,
        language,
        preserve_meaning: preserveMeaning,
        stream: true,
      };

      if (this.activeProjectId) {
        payload.project_id = this.activeProjectId;
      }
      
      // If XML is provided, include it in the request
      if (paragraphXml) {
        payload.paragraph_xml = paragraphXml;
        console.log('[api.ts] Including paragraph_xml in streaming polish request:', {
          xmlLength: paragraphXml.length,
          xmlPreview: paragraphXml.substring(0, 200)
        });
      }
      
      // Use fetch for streaming
      const url = `${this.baseURL}/add-ins/polish`;
      console.log('[api.ts] Starting streaming polish request to:', url);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      this.applyFetchAuthHeaders(headers);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const usageLimit = await notifyUsageLimitFromFetchResponse(response);
      if (usageLimit) {
        throw new CostLimitExceededError(usageLimit);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      if (!response.body) {
        throw new Error('Response body is null');
      }
      
      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      console.log('[api.ts] Starting to read SSE stream');
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('[api.ts] Stream reading complete');
          // Process any remaining buffer
          if (buffer.trim()) {
            const lines = buffer.split('\n\n');
            for (const line of lines) {
              if (line.trim() && line.startsWith('data: ')) {
                const data = line.substring(6).trim();
                if (data) {
                  try {
                    const parsed = JSON.parse(data);
                    console.log('[api.ts] Received final SSE chunk:', parsed.type);
                    yield parsed;
                  } catch (e) {
                    console.warn('[api.ts] Failed to parse final SSE data:', data, e);
                  }
                }
              }
            }
          }
          break;
        }
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        console.log('[api.ts] Received raw chunk:', chunk.length, 'chars, buffer size:', buffer.length);
        
        // Process complete SSE messages (SSE messages end with \n\n)
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n\n')) !== -1) {
          const message = buffer.substring(0, newlineIndex);
          buffer = buffer.substring(newlineIndex + 2);
          
          // SSE format: "data: <json>\n\n"
          if (message.startsWith('data: ')) {
            const data = message.substring(6).trim();
            if (data) {
              try {
                const parsed = JSON.parse(data);
                console.log('[api.ts] Parsed SSE chunk:', {
                  type: parsed.type,
                  hasContent: !!parsed.content,
                  contentLength: parsed.content?.length || 0,
                  hasProcessedText: !!parsed.processed_text,
                  hasParagraphXml: !!parsed.paragraph_xml
                });
                yield parsed;
              } catch (e) {
                console.warn('[api.ts] Failed to parse SSE data:', data, e);
              }
            }
          } else if (message.trim()) {
            // Handle messages without "data: " prefix (shouldn't happen but be safe)
            console.warn('[api.ts] Received SSE message without "data: " prefix:', message.substring(0, 100));
          }
        }
      }
      
    } catch (error: any) {
      console.error('Error polishing text with streaming:', error);
      yield {
        type: 'error',
        error: error.message || 'Failed to polish text with streaming'
      };
    }
  }

  /**
   * Chat-based query for natural language text editing
   * Handles conversational queries like "make it mutual", "polish it", etc.
   * Language is automatically detected from the text
   */
  async chatQuery(
    text: string,
    query: string,
    context?: string,
    reviewPartyContext?: string
  ): Promise<ApiResponse<{ response: string; processed_text: string; confidence?: number }>> {
    try {
      // Auto-detect language from text
      const language = detectLanguage(text);

      // Use longer timeout for LLM operations (120 seconds)
      const response = await this.axiosInstance.post('/add-ins/chat', {
        text,
        query,
        context,
        language,
        review_party_context: reviewPartyContext?.trim() || undefined,
      }, {
        timeout: 120000, // 120 seconds for LLM processing
      });
      return response.data;
    } catch (error: any) {
      console.error('Error processing chat query:', error);
      throw error;
    }
  }

  /**
   * Create a new sub-project
   */
  async createSubProject(
    projectId: string,
    name: string,
    description?: string,
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<SubProject>> {
    try {
      // Create is a project-level call, so there is no sub-project id to key the cache on
      // yet; route purely by the UI's domain hint (definitive) or probe both when unknown.
      const { domains, definitive } = this.resolveDomains(undefined, domainHint);

      let firstMeaningfulError: any = null;
      for (const domain of domains) {
        try {
          const response = await this.axiosInstance.post(
            `/${domain}/projects/${projectId}/sub-projects`,
            { name, description, parent_project_id: projectId },
          );
          if (response.data.success) {
            // Learn the domain for the freshly created sub-project so every subsequent
            // call in the flow (copy/upload/identify/save/auto-start) skips the probe.
            const newId = response.data?.data?.id;
            if (newId) this.rememberSubProjectDomain(newId, domain);
            return response.data;
          }
        } catch (err: any) {
          if (definitive) throw this.formatAxiosError(err, 'Create sub-project endpoint not found');
          const status = err?.response?.status;
          if (!firstMeaningfulError && status !== 404 && status !== 400) firstMeaningfulError = err;
          continue;
        }
      }

      if (firstMeaningfulError) {
        throw this.formatAxiosError(firstMeaningfulError, 'Create sub-project endpoint not found');
      }
      throw new Error('Create sub-project endpoint not found');
    } catch (error: any) {
      console.error('Error creating sub-project:', error);
      throw error;
    }
  }

  /**
   * Identify sides from track changes (Module B - Step 1)
   */
  async identifySides(subProjectId: string, domainHint?: ApiDomain): Promise<string[]> {
    try {
      const data = await this.requestWithDomain(
        subProjectId,
        domainHint,
        'Identify sides endpoint not found',
        (domain) => this.axiosInstance.post(`/${domain}/sub-projects/${subProjectId}/identify-sides`),
      );
      return Array.isArray(data.data) ? data.data : [];
    } catch (error: any) {
      console.error('Error identifying sides:', error);
      throw error;
    }
  }

  /**
   * Save side assignments (Module B - Step 1)
   */
  async saveSideAssignments(
    subProjectId: string,
    sideAssignments: Record<string, 'our_side' | 'their_side'>,
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<any>> {
    try {
      return await this.requestWithDomain(
        subProjectId,
        domainHint,
        'Save side assignments endpoint not found',
        (domain) =>
          this.axiosInstance.post(`/${domain}/sub-projects/${subProjectId}/save-side-assignments`, {
            side_assignments: sideAssignments,
          }),
      );
    } catch (error: any) {
      console.error('Error saving side assignments:', error);
      throw error;
    }
  }

  /**
   * Get side assignments (Module B - Step 1)
   */
  async getSideAssignments(
    subProjectId: string,
    domainHint?: ApiDomain,
  ): Promise<Record<string, 'our_side' | 'their_side'>> {
    // Prefer the known domain; only probe both when origin is unknown. This is a
    // best-effort read, so any failure resolves to {} rather than throwing.
    const { domains } = this.resolveDomains(subProjectId, domainHint);
    for (const domain of domains) {
      try {
        const response = await this.axiosInstance.get(
          `/${domain}/sub-projects/${subProjectId}/side-assignments`,
        );
        if (response.data.success && response.data.data) {
          this.rememberSubProjectDomain(subProjectId, domain);
          return response.data.data;
        }
      } catch (err) {
        if (
          (err as AxiosError & { isAuthError?: boolean })?.isAuthError ||
          (err as AxiosError)?.response?.status === 401
        ) {
          throw err;
        }
        continue;
      }
    }
    return {};
  }

  /**
   * Upload a document file to a sub-project
   */
  async uploadDocument(
    projectId: string,
    subProjectId: string,
    file: File,
    fileType: string = 'Client Requests',
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<any>> {
    try {
      const buildFormData = () => {
        const formData = new FormData();
        formData.append('project_id', projectId);
        formData.append('sub_project_id', subProjectId);
        formData.append('file_type', fileType);
        formData.append('file', file);
        return formData;
      };

      return await this.requestWithDomain(
        subProjectId,
        domainHint,
        'Upload document endpoint not found',
        (domain) =>
          this.axiosInstance.post(`/${domain}/upload/document`, buildFormData(), {
            headers: { 'Content-Type': 'multipart/form-data' },
          }),
      );
    } catch (error: any) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  /**
   * Analyze track changes in an uploaded file (matches dashboard ai-tools endpoint).
   */
  async analyzeTrackChanges(
    filePath: string,
    projectId?: string,
    fileId?: string
  ): Promise<ApiResponse<{ paragraphs_with_track_changes?: number }>> {
    try {
      const formData = new FormData();
      formData.append('file_path', filePath);
      if (fileId) {
        formData.append('file_id', fileId);
      }

      let url = '/ai-tools/analyze-track-changes';
      if (projectId) {
        url += `?project_id=${encodeURIComponent(projectId)}`;
      }

      const response = await this.axiosInstance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error analyzing track changes:', error);
      throw error;
    }
  }

  /**
   * Construction Loan Agreement parent projects that have at least one playbook row
   * (Past Contracts). Narrow list for the Word add-in playbook picker.
   */
  async getConstructionLoanPlaybookParentProjects(): Promise<
    ApiResponse<Array<{ id: string; name: string }>>
  > {
    try {
      const response = await this.axiosInstance.get(
        '/construction-loan/playbook-parent-projects',
        { timeout: 30000 }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching playbook parent projects:', error);
      throw error;
    }
  }

  /**
   * Load all past-contract playbook rows in 500-record pages (Cloud Run response limit).
   */
  private async fetchPaginatedPastContractPlaybooks(path: string): Promise<Array<Record<string, any>>> {
    const batchSize = 500;
    const all: Array<Record<string, any>> = [];
    let offset = 0;

    for (;;) {
      const res = await this.axiosInstance.get(path, {
        params: { limit: batchSize, offset },
        timeout: 120000,
      });
      const data = res.data?.data;
      if (Array.isArray(data)) {
        return data;
      }
      const items = data?.items;
      if (!Array.isArray(items)) {
        break;
      }
      all.push(...items);
      if (!data.has_more || items.length === 0) {
        break;
      }
      offset += items.length;
    }

    return all;
  }

  /**
   * List playbook rows for the Agent playbook picker.
   *
   * Merges results from Past Contracts (construction loan) and Real Estate playbooks
   * endpoints (deduped by row id). Uses paragraph_text, or falls back to stripping
   * paragraph_xml. Order of calls follows workflowHint when set; always tries the
   * other source if the first returns no usable rows so users still see data when
   * the solution tab guess is wrong.
   */
  async listPlaybooksForAgent(
    projectId: string,
    workflowHint: 'construction_loan' | 'real_estate' | undefined
  ): Promise<Array<{
    id: string;
    paragraph_text: string;
    paragraph_preview: string;
    filename: string;
    author: string;
    side?: string;
  }>> {
    type Row = {
      id: string;
      paragraph_text: string;
      paragraph_preview: string;
      filename: string;
      author: string;
      side?: string;
    };

    const toRow = (r: Record<string, any>): Row | null => {
      const plain = playbookParagraphPlainText(r);
      if (!plain) return null;
      return {
        id: String(r.id),
        paragraph_text: plain,
        paragraph_preview: plain.slice(0, 160).replace(/\s+/g, ' ').trim(),
        filename: String(r.filename || r.file_id || '—'),
        author: String(r.author || '—'),
        side: r.side ?? undefined,
      };
    };

    const mergeInto = (rows: Array<Record<string, any>> | undefined, map: Map<string, Row>) => {
      if (!Array.isArray(rows)) return;
      for (const r of rows) {
        const row = toRow(r);
        if (row && !map.has(row.id)) map.set(row.id, row);
      }
    };

    const byId = new Map<string, Row>();

    const fetchConstruction = async (): Promise<void> => {
      try {
        const rows = await this.fetchPaginatedPastContractPlaybooks(
          `/construction-loan/projects/${projectId}/past-contracts/playbooks`
        );
        mergeInto(rows, byId);
      } catch {
        /* wrong domain or network — ignore */
      }
    };

    const fetchRealEstateFinal = async (): Promise<void> => {
      try {
        const res = await this.axiosInstance.get(
          `/real-estate/projects/${projectId}/playbooks/final`,
          { timeout: 30000 }
        );
        if (res.data?.success && Array.isArray(res.data?.data)) {
          mergeInto(res.data.data, byId);
        }
      } catch {
        /* ignore */
      }
    };

    const fetchRealEstateAll = async (): Promise<void> => {
      try {
        const res = await this.axiosInstance.get(
          `/real-estate/projects/${projectId}/playbooks`,
          { timeout: 30000 }
        );
        if (res.data?.success && Array.isArray(res.data?.data)) {
          mergeInto(res.data.data, byId);
        }
      } catch {
        /* ignore */
      }
    };

    try {
      if (workflowHint === 'construction_loan') {
        await fetchConstruction();
        if (byId.size === 0) {
          await fetchRealEstateFinal();
          if (byId.size === 0) await fetchRealEstateAll();
        }
      } else {
        await fetchRealEstateFinal();
        if (byId.size === 0) await fetchRealEstateAll();
        if (byId.size === 0) await fetchConstruction();
      }

      return Array.from(byId.values());
    } catch (error: any) {
      console.error('Error listing playbooks for agent:', error);
      return Array.from(byId.values());
    }
  }

  /**
   * Resolve past-contract playbook records by ID (Construction Loan).
   * Returns paragraph text, paragraph XML, side, file_id, and source_filename (files table) for each matching ID.
   */
  async getPastContractsPlaybooksByIds(
    projectId: string,
    ids: string[],
    domain: 'construction-loan' | 'real-estate' = 'construction-loan'
  ): Promise<
    ApiResponse<
      Array<{
        id: string;
        paragraph_text: string;
        paragraph_xml?: string;
        side?: string;
        file_id?: string;
        source_filename?: string;
        source_doc_type?: string;
        paragraph_index?: number | null;
        author?: string;
        timestamp?: string;
      }>
    >
  > {
    if (!ids.length) {
      return { success: true, data: [] };
    }
    // Try the hinted domain first, then the other. Mirrors getSubProjectDetails so
    // the call succeeds regardless of whether detectedDomain state is ready yet.
    const domains: Array<'construction-loan' | 'real-estate'> =
      domain === 'real-estate'
        ? ['real-estate', 'construction-loan']
        : ['construction-loan', 'real-estate'];
    let lastError: any;
    for (const prefix of domains) {
      try {
        const response = await this.axiosInstance.post(
          `/${prefix}/projects/${projectId}/past-contracts/playbooks/by-ids`,
          { ids },
          { timeout: 120000 }
        );
        if (response.data?.success) return response.data;
      } catch (err: any) {
        lastError = err;
      }
    }
    console.error('Error getting past contracts playbooks by ids:', lastError);
    throw lastError;
  }

  /**
   * Export the Sales Agreement source file (PDF or DOCX→PDF) for a sub-project and
   * fuzzy-highlight the matched clause text on the appropriate page. Mirrors
   * getPastNegotiationSourcePdf so callers can render the result with
   * `PastNegotiationSourcePdfPanel`.
   *
   * The endpoint lives under /real-estate. We try both domain prefixes for
   * symmetry with getSubProjectDetails / getPastNegotiationSourcePdf so the call
   * still succeeds when the client-side detectedDomain hasn't propagated yet.
   */
  async getSalesAgreementSourcePdf(
    subProjectId: string,
    text: string,
    domain: 'construction-loan' | 'real-estate' = 'real-estate'
  ): Promise<{ blob: Blob; highlightPage: number; totalPages: number }> {
    const domains: Array<'construction-loan' | 'real-estate'> =
      domain === 'real-estate'
        ? ['real-estate', 'construction-loan']
        : ['construction-loan', 'real-estate'];
    let lastError: any;
    for (const prefix of domains) {
      try {
        const response = await this.axiosInstance.post(
          `/${prefix}/sub-projects/${subProjectId}/sales-agreement/source-pdf`,
          { text },
          { responseType: 'blob', timeout: 600000 }
        );
        const h = response.headers?.['x-highlight-page'] ?? response.headers?.['X-Highlight-Page'];
        const t =
          response.headers?.['x-highlight-total-pages'] ??
          response.headers?.['X-Highlight-Total-Pages'];
        return {
          blob: response.data as Blob,
          highlightPage: parseInt(typeof h === 'string' ? h : '1', 10) || 1,
          totalPages: parseInt(typeof t === 'string' ? t : '1', 10) || 1,
        };
      } catch (err: any) {
        lastError = err;
      }
    }
    throw lastError;
  }

  /**
   * Fuzzy-match clauses against an in-memory PDF and return per-clause page +
   * bounding-box coordinates. Mirrors the dashboard `/pdf-previewer/highlight-clauses`
   * call used by `PdfViewer.highlightClauses`. Used by the Sales Agreement
   * "Go to" flow so the panel can scroll to the matched rect within the page,
   * not just to the page top.
   */
  async highlightClausesInPdf(
    pdfBlob: Blob,
    clauses: Array<{ id: string; text: string; clause_number?: string }>,
    options: { filename?: string; timeoutMs?: number } = {}
  ): Promise<{
    success: boolean;
    results: Record<
      string,
      Array<{
        page: number;
        rect: { x0: number; y0: number; x1: number; y1: number };
        text: string;
        similarity: number;
      }>
    >;
  }> {
    const formData = new FormData();
    formData.append('file', pdfBlob, options.filename ?? 'document.pdf');
    formData.append('clauses', JSON.stringify(clauses));
    const response = await this.axiosInstance.post(
      '/pdf-previewer/highlight-clauses',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: options.timeoutMs ?? 120000,
      }
    );
    return response.data;
  }

  /**
   * Export past-contract playbook source DOCX to PDF (Google service account), with paragraph highlighted.
   */
  async getPastNegotiationSourcePdf(
    projectId: string,
    playbookId: string,
    domain: 'construction-loan' | 'real-estate' = 'construction-loan',
    searchText?: string
  ): Promise<{ blob: Blob; highlightPage: number; totalPages: number }> {
    // Try the hinted domain first, then the other, so the call self-corrects when
    // detectedDomain state hasn't propagated yet.
    const domains: Array<'construction-loan' | 'real-estate'> =
      domain === 'real-estate'
        ? ['real-estate', 'construction-loan']
        : ['construction-loan', 'real-estate'];
    let lastError: any;
    for (const prefix of domains) {
      try {
        const response = await this.axiosInstance.post(
          `/${prefix}/projects/${projectId}/past-contracts/playbooks/source-pdf`,
          {
            playbook_id: playbookId,
            ...(searchText?.trim() ? { text: searchText.trim() } : {}),
          },
          { responseType: 'blob', timeout: 600000 }
        );
        const h = response.headers?.['x-highlight-page'] ?? response.headers?.['X-Highlight-Page'];
        const t =
          response.headers?.['x-highlight-total-pages'] ??
          response.headers?.['X-Highlight-Total-Pages'];
        return {
          blob: response.data as Blob,
          highlightPage: parseInt(typeof h === 'string' ? h : '1', 10) || 1,
          totalPages: parseInt(typeof t === 'string' ? t : '1', 10) || 1,
        };
      } catch (err: any) {
        lastError = err;
      }
    }
    throw lastError;
  }

  /**
   * Extract entity names from document text
   * Used for Review mode to identify parties in the document
   */
  async extractEntities(text: string): Promise<string[]> {
    try {
      const response = await this.axiosInstance.post('/word-addins/extract-entities', {
        text: text,
        max_entities: 20
      }, {
        timeout: 120000, // 120 seconds for entity extraction (LLM processing can take time)
      });
      
      // Backend returns ApiResponse format: { success: true, message: "...", data: [...] }
      if (response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      console.warn('Unexpected response format from extract-entities:', response.data);
      return [];
    } catch (error: any) {
      console.error('Error extracting entities:', error);
      // Return empty array on error rather than throwing
      return [];
    }
  }

  /**
   * Generate bank-of-change suggestion with streaming (master cross check).
   * Uses client_request_text, client_request_xml, and the selected bank_of_changes_text.
   * Only suggested text is streamed (no explanation or key_differences).
   */
  async *generateBankOfChangeSuggestionStream(
    projectId: string,
    clientRequestText: string,
    clientRequestXml: string,
    bankOfChangesText: string,
    sourceType: 'bank_of_changes' | 'past_agreement' | 'both' = 'bank_of_changes',
    referenceExplanation?: string
  ): AsyncGenerator<{ type: string; content?: string; suggested_text?: string; suggested_xml?: string; error?: string }, void, unknown> {
    try {
      const payload: Record<string, unknown> = {
        project_id: projectId,
        client_request_text: clientRequestText,
        client_request_xml: clientRequestXml,
        bank_of_changes_text: bankOfChangesText,
        source_type: sourceType,
        stream: true,
      };
      if (referenceExplanation) {
        payload.reference_explanation = referenceExplanation;
      }

      const url = `${this.baseURL}/real-estate/generate-bank-of-change-suggestion-stream`;
      console.log('[api.ts] Starting bank-of-change suggestion stream to:', url);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      this.applyFetchAuthHeaders(headers);

      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const usageLimit = await notifyUsageLimitFromFetchResponse(response);
      if (usageLimit) {
        throw new CostLimitExceededError(usageLimit);
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            const lines = buffer.split('\n\n');
            for (const line of lines) {
              if (line.trim() && line.startsWith('data: ')) {
                const data = line.substring(6).trim();
                if (data) {
                  try {
                    yield JSON.parse(data);
                  } catch (e) {
                    console.warn('[api.ts] Failed to parse final bank-of-change SSE data:', data, e);
                  }
                }
              }
            }
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n\n')) !== -1) {
          const message = buffer.substring(0, newlineIndex);
          buffer = buffer.substring(newlineIndex + 2);
          if (message.startsWith('data: ')) {
            const data = message.substring(6).trim();
            if (data) {
              try {
                yield JSON.parse(data);
              } catch (e) {
                console.warn('[api.ts] Failed to parse bank-of-change SSE data:', data, e);
              }
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error generating bank-of-change suggestion stream:', error);
      yield { type: 'error', error: error.message || 'Failed to generate bank-of-change suggestion' };
    }
  }

  /**
   * Generate insight-based responses with streaming (MCC "AI - Already Exist").
   * Mirrors dashboard `generateInsightResponsesStream`.
   */
  async *generateInsightResponsesStream(
    subProjectId: string,
    clientRequest: string,
    insights: Array<Record<string, unknown>>,
  ): AsyncGenerator<
    { type: string; content?: string; suggestions?: Array<{ suggestedResponse?: string }>; error?: string },
    void,
    unknown
  > {
    try {
      const url = `${this.baseURL}/real-estate/sub-projects/${subProjectId}/generate-insight-responses`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      this.applyFetchAuthHeaders(headers);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          client_request: clientRequest,
          insights,
        }),
      });

      const usageLimit = await notifyUsageLimitFromFetchResponse(response);
      if (usageLimit) {
        throw new CostLimitExceededError(usageLimit);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (!response.body) {
        throw new Error('Response body is not readable');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            const lines = buffer.split('\n\n');
            for (const line of lines) {
              if (line.trim() && line.startsWith('data: ')) {
                const data = line.substring(6).trim();
                if (data) {
                  try {
                    yield JSON.parse(data);
                  } catch (e) {
                    console.warn('[api.ts] Failed to parse final insight SSE data:', data, e);
                  }
                }
              }
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n\n')) !== -1) {
          const message = buffer.substring(0, newlineIndex);
          buffer = buffer.substring(newlineIndex + 2);
          if (message.startsWith('data: ')) {
            const data = message.substring(6).trim();
            if (data) {
              try {
                yield JSON.parse(data);
              } catch (e) {
                console.warn('[api.ts] Failed to parse insight SSE data:', data, e);
              }
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error in streaming insight responses:', error);
      yield { type: 'error', error: error.message || 'Failed to generate insight responses' };
    }
  }

  /**
   * Copy master documents (Sales Agreement, Bank of Changes, etc.) from parent project to a sub-project
   */
  async copyMasterDocumentsToSubProject(
    subProjectId: string,
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<any[]>> {
    try {
      return (await this.requestWithDomain(
        subProjectId,
        domainHint,
        'Copy master documents endpoint not found',
        (domain) =>
          this.axiosInstance.post(`/${domain}/sub-projects/${subProjectId}/copy-master-documents`, {}),
      )) as ApiResponse<any[]>;
    } catch (error: any) {
      console.error('Error copying master documents:', error);
      throw error;
    }
  }

  /**
   * Get project detail including sub_projects list
   */
  async getProjectDetail(projectId: string, domainHint?: ApiDomain): Promise<ApiResponse<any>> {
    try {
      // Project-level call (no sub-project cache key); route by the UI hint when known,
      // otherwise probe both. A definitive hint surfaces the real error on failure.
      const definitive = !!domainHint;
      const domains: ApiDomain[] = domainHint
        ? [domainHint]
        : ['construction-loan', 'real-estate'];

      for (const domain of domains) {
        let response: AxiosResponse<any>;
        try {
          response = await this.axiosInstance.get(`/${domain}/projects/${projectId}`);
        } catch (err: any) {
          if (definitive) throw this.formatAxiosError(err, 'Project detail endpoint not found');
          continue;
        }

        if (response.data.success) {
          return response.data;
        }
        // 200 with success=false: always surface the backend's own explanation.
        // This is outside the catch block so it cannot be swallowed by probe-mode continue.
        const data = response.data as { message?: string; detail?: string } | undefined;
        const backendMsg = data?.detail || data?.message;
        throw new Error(typeof backendMsg === 'string' && backendMsg.trim() ? backendMsg : 'Project detail endpoint not found');
      }

      throw new Error('Project detail endpoint not found');
    } catch (error: any) {
      console.error('Error fetching project detail:', error);
      throw error;
    }
  }

  /**
   * Start the AI cross-check process for a sub-project (creates smart cards for client requests)
   */
  async startCrossCheck(subProjectId: string): Promise<ApiResponse<any>> {
    try {
      const endpoints = [
        `/construction-loan/sub-projects/${subProjectId}/cross-check-documents`,
        `/real-estate/sub-projects/${subProjectId}/cross-check-documents`,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await this.axiosInstance.post(endpoint, {}, { timeout: ApiService.LONG_REQUEST_TIMEOUT_MS });
          if (response.data.success) {
            return response.data;
          }
        } catch (err: any) {
          continue;
        }
      }

      throw new Error('Start cross-check endpoint not found');
    } catch (error: any) {
      console.error('Error starting cross-check:', error);
      throw error;
    }
  }

  /**
   * Auto-start the full cross-check workflow (Step 1 Identify + Step 2 Assign + Step 3 Cross-Check).
   * For Construction Loan, supply review_mode (redlines vs first-pass), optional paragraphs
   * for first-pass, or pre-extracted track-change paragraphs for redlines mode.
   * Master Cross Checks uses the default real-estate workflow without review_mode.
   *
   * Both backends now spawn the workflow as a background task and return
   * `{ data: { status: "queued", ... } }` within seconds. The Word add-in
   * relies on the existing `getSubProjectDetails` polling loop to track
   * progress and completion, so we no longer hold the HTTP connection open
   * for the full workflow duration (which was pinning Cloud Run workers).
   */
  async autoStartCrossCheck(
    subProjectId: string,
    payload?: {
      review_mode?: 'review_and_respond_redlines' | 'first_pass_legal_review';
      paragraphs?: Array<{ paragraph_index: number; text: string }>;
      pre_extracted_paragraphs?: Array<{
        paragraph_index: number;
        paragraph_xml: string;
        original_text: string;
        edited_text: string;
        participants: string[];
      }>;
      all_paragraph_texts?: string[];
    },
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<any>> {
    try {
      const body = payload ?? { review_mode: 'review_and_respond_redlines' };
      let firstMeaningfulError: any = null;

      // A Construction Loan payload is itself a strong domain signal; fold it into the
      // hint so we route definitively when the caller did not pass an explicit domain.
      const isConstructionLoanPayload = Boolean(
        payload &&
        (
          payload.review_mode === 'first_pass_legal_review' ||
          payload.review_mode === 'review_and_respond_redlines' ||
          'pre_extracted_paragraphs' in payload ||
          'paragraphs' in payload
        )
      );
      const effectiveHint: ApiDomain | undefined =
        domainHint ?? (isConstructionLoanPayload ? 'construction-loan' : undefined);

      // Prefer the cached/known domain or hint (single call); otherwise probe both.
      const { domains } = this.resolveDomains(subProjectId, effectiveHint);
      const endpoints = domains.map(
        (domain) => `/${domain}/sub-projects/${subProjectId}/auto-start-cross-check`,
      );

      for (const endpoint of endpoints) {
        try {
          // The backend now spawns the workflow as a background task and
          // returns immediately, so a short timeout is sufficient. 60s leaves
          // headroom for synchronous validation (access check, usage limits,
          // initial DB write) under load. The 120-min `LONG_REQUEST_TIMEOUT_MS`
          // is no longer needed and would only delay error surfacing if the
          // request stalled mid-handler.
          const response = await this.axiosInstance.post(endpoint, body, { timeout: 60_000 });
          if (response.data.success) {
            const matched = endpoint.split('/')[1] as ApiDomain;
            this.rememberSubProjectDomain(subProjectId, matched);
            return response.data;
          }

          // Endpoint responded with 200 but declared failure.
          // Treat as a real error instead of probing other domains, otherwise
          // we can mask the root cause and invoke the wrong workflow.
          const detail =
            response.data?.message ||
            response.data?.error ||
            response.data?.detail ||
            'Auto-start cross-check failed';
          throw new Error(typeof detail === 'string' ? detail : 'Auto-start cross-check failed');
        } catch (err: any) {
          const status = err?.response?.status;
          const detail =
            err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Unknown error';

          // Keep the first useful error so we can surface a real cause if all endpoints fail.
          if (!firstMeaningfulError && status !== 404) {
            firstMeaningfulError = err;
          }

          // For server/runtime failures, don't mask the root cause behind fallback probing.
          if (typeof status === 'number' && status >= 500) {
            throw new Error(typeof detail === 'string' ? detail : 'Auto-start cross-check failed');
          }

          continue;
        }
      }

      if (firstMeaningfulError) {
        const detail =
          firstMeaningfulError?.response?.data?.detail ||
          firstMeaningfulError?.response?.data?.error ||
          firstMeaningfulError?.response?.data?.message ||
          firstMeaningfulError?.message;
        throw new Error(detail || 'Auto-start cross-check failed');
      }

      throw new Error('Auto-start cross-check endpoint not found');
    } catch (error: any) {
      console.error('Error auto-starting cross-check:', error);
      throw error;
    }
  }

  /**
   * Save inline loan agreement context (reject-all semantics) to the backend.
   * Called automatically when the user creates a Construction Loan client project
   * from the Word add-in.
   */
  async saveLoanAgreementContext(
    subProjectId: string,
    fullText: string,
    paragraphs: Array<{ index: number; text: string }>
  ): Promise<ApiResponse<any>> {
    const response = await this.axiosInstance.post(
      `/construction-loan/sub-projects/${subProjectId}/loan-agreement-context`,
      { full_text: fullText, paragraphs }
    );
    return response.data;
  }

  /**
   * Save inline sales agreement context (reject-all semantics) for Master Cross Checks
   * first-pass legal review before auto-start Step 3.
   */
  async saveSalesAgreementInlineContext(
    subProjectId: string,
    fullText: string,
    paragraphs: Array<{ index: number; text: string }>
  ): Promise<ApiResponse<any>> {
    const response = await this.axiosInstance.post(
      `/real-estate/sub-projects/${subProjectId}/sales-agreement-inline-context`,
      { full_text: fullText, paragraphs }
    );
    return response.data;
  }

  /**
   * Link a draft sub-project to a parent sub-project (for draft iterations)
   */
  async linkToParent(
    subProjectId: string,
    parentSubProjectId: string,
    domainHint?: ApiDomain,
  ): Promise<ApiResponse<any>> {
    try {
      return await this.requestWithDomain(
        subProjectId,
        domainHint,
        'Link to parent endpoint not found',
        (domain) =>
          this.axiosInstance.post(`/${domain}/sub-projects/${subProjectId}/link-to-parent`, {
            parent_sub_project_id: parentSubProjectId,
          }),
      );
    } catch (error: any) {
      console.error('Error linking sub-project to parent:', error);
      throw error;
    }
  }

  /**
   * Proofread document for language issues
   */
  async proofreadDocument(
    text: string,
    paragraphs: Array<{ index: number; text: string }>
  ): Promise<{
    definitions: Array<{ term: string; type: string; occurrences: Array<{ paragraphIndex: number; snippet: string }> }>;
    draftingNotes: Array<{ type: string; text: string; paragraphIndex: number }>;
    language: Array<{ originalText: string; suggestion: string; explanation: string; paragraphIndex: number }>;
  }> {
    const empty = { definitions: [], draftingNotes: [], language: [] };
    try {
      const startedAt = Date.now();
      log('[ProofreadFlow] api.proofreadDocument:request', {
        endpoint: '/word-addins/proofread-document',
        paragraphCount: paragraphs.length,
        textLength: text.length,
      });
      const response = await this.axiosInstance.post(
        '/word-addins/proofread-document',
        { text, paragraphs },
        { timeout: 150000 }
      );
      const d = response?.data?.data;
      log('[ProofreadFlow] api.proofreadDocument:response', {
        success: response?.data?.success ?? false,
        definitionsCount: d?.definitions?.length ?? 0,
        draftingNotesCount: d?.draftingNotes?.length ?? 0,
        languageCount: d?.language?.length ?? 0,
        totalMs: Date.now() - startedAt,
      });
      if (response.data.success && d) {
        return {
          definitions: d.definitions || [],
          draftingNotes: d.draftingNotes || [],
          language: d.language || [],
        };
      }
      log('[ProofreadFlow] api.proofreadDocument:empty_or_unsuccessful_payload');
      return empty;
    } catch (error: any) {
      logError('[ProofreadFlow] api.proofreadDocument:error', {
        message: error?.message || String(error),
        status: error?.response?.status,
        detail: error?.response?.data,
      });
      throw error;
    }
  }

  /**
   * Agent mode: generate structured issue suggestions from document + goal.
   * Never edits the document — only returns actionable issues.
   */
  async agentGenerateIssues(
    goal: string,
    text: string,
    paragraphs: Array<{ index: number; text: string }>,
    reviewPartyContext?: string
  ): Promise<Array<{
    paragraphIndex: number;
    originalText: string;
    suggestedText: string;
    explanation: string;
    category: string;
    severity: string;
    groupId?: string;
  }>> {
    try {
      const startedAt = Date.now();
      log('[AgentFlow] api.agentGenerateIssues:request', {
        endpoint: '/add-ins/agent-generate-issues',
        paragraphCount: paragraphs.length,
        textLength: text.length,
        goalLength: goal.length,
      });
      const body: {
        goal: string;
        document_text: string;
        paragraphs: Array<{ index: number; text: string }>;
        review_party_context?: string;
      } = { goal, document_text: text, paragraphs };
      if (reviewPartyContext?.trim()) {
        body.review_party_context = reviewPartyContext.trim();
      }
      const response = await this.axiosInstance.post(
        '/add-ins/agent-generate-issues',
        body,
        { timeout: 180000 }
      );
      const d = response?.data?.data;
      log('[AgentFlow] api.agentGenerateIssues:response', {
        success: response?.data?.success ?? false,
        issuesCount: d?.issues?.length ?? 0,
        totalMs: Date.now() - startedAt,
      });
      if (response.data.success && d) {
        return d.issues || [];
      }
      log('[AgentFlow] api.agentGenerateIssues:empty_or_unsuccessful');
      return [];
    } catch (error: any) {
      logError('[AgentFlow] api.agentGenerateIssues:error', {
        message: error?.message || String(error),
        status: error?.response?.status,
        detail: error?.response?.data,
      });
      throw error;
    }
  }

  /**
   * Agent orchestration step: tool calls (client-executed) or final issues + plan + narration.
   */
  async agentOrchestrateStep(params: {
    goal: string;
    documentText: string;
    paragraphs: Array<{ index: number; text: string }>;
    reviewPartyContext?: string;
    followUp?: string;
    /** Short summary fallback when structured prior rounds are not sent */
    previousIssuesDigest?: string;
    /** Full prior rounds for follow-up revision (user_message, round_summary, issues) */
    previousRoundsContext?: Array<{
      user_message: string;
      round_summary: string;
      issues: Array<{
        paragraphIndex: number;
        originalText: string;
        suggestedText: string;
        explanation: string;
        category: string;
        severity: string;
        groupId?: string;
        status?: string;
      }>;
    }>;
    toolResults?: Array<{ id: string; name: string; result?: string; error?: string }>;
    iterationIndex: number;
    /** First iteration’s plan; send on iter > 0 so the engine keeps the same step text */
    frozenPlan?: string[];
    /** Word add-in: suggestions = review/issues; draft = insert_paragraphs + structure */
    agentMode?: 'suggestions' | 'draft';
    /** First orchestration step only; reference files for this user message */
    attachments?: Array<{ filename: string; content_base64: string; mime_type?: string }>;
    /** First orchestration step only; playbook / past-contract precedent text */
    playbookContext?: string;
    // ↑ Added after attachments; sent to engine as playbook_context
  }): Promise<{
    phase: 'need_tools' | 'final';
    plan: string[];
    planStepCompleted: number;
    narration: string;
    toolCalls: Array<{ id: string; name: string; arguments: Record<string, unknown> }>;
    issues: Array<{
      paragraphIndex: number;
      originalText: string;
      suggestedText: string;
      explanation: string;
      category: string;
      severity: string;
      groupId?: string;
    }>;
    roundSummary: string;
  }> {
    const body: Record<string, unknown> = {
      goal: params.goal,
      document_text: params.documentText,
      paragraphs: params.paragraphs,
      iteration_index: params.iterationIndex,
    };
    if (params.reviewPartyContext?.trim()) {
      body.review_party_context = params.reviewPartyContext.trim();
    }
    if (params.followUp?.trim()) {
      body.follow_up = params.followUp.trim();
    }
    if (params.previousIssuesDigest?.trim()) {
      body.previous_issues_digest = params.previousIssuesDigest.trim();
    }
    if (params.previousRoundsContext && params.previousRoundsContext.length > 0) {
      body.previous_rounds_context = params.previousRoundsContext;
    }
    if (params.toolResults && params.toolResults.length > 0) {
      body.tool_results = params.toolResults.map((t) => ({
        id: t.id,
        name: t.name,
        result: t.result,
        error: t.error,
      }));
    }
    if (params.iterationIndex > 0 && params.frozenPlan && params.frozenPlan.length > 0) {
      body.frozen_plan = params.frozenPlan;
    }
    body.agent_mode = params.agentMode ?? 'suggestions';
    if (
      params.iterationIndex === 0 &&
      params.attachments &&
      params.attachments.length > 0
    ) {
      body.attachments = params.attachments;
    }
    if (params.iterationIndex === 0 && params.playbookContext?.trim()) {
      body.playbook_context = params.playbookContext.trim();
    }

    const response = await this.axiosInstance.post('/add-ins/agent-orchestrate-step', body, {
      timeout: 180000,
    });
    const d = response?.data?.data;
    if (!response.data.success || !d) {
      log('[AgentFlow] api.agentOrchestrateStep:empty_or_unsuccessful');
      throw new Error(response?.data?.message || 'Agent step failed');
    }
    return {
      phase: d.phase === 'need_tools' ? 'need_tools' : 'final',
      plan: d.plan || [],
      planStepCompleted: d.plan_step_completed ?? 0,
      narration: d.narration || '',
      toolCalls: (d.tool_calls || []).map((t: { id: string; name: string; arguments?: Record<string, unknown> }) => ({
        id: t.id,
        name: t.name,
        arguments: t.arguments || {},
      })),
      issues: d.issues || [],
      roundSummary: d.round_summary || '',
    };
  }

  /**
   * Review document with context
   */
  async reviewDocument(
    text: string,
    paragraphs: Array<{index: number, text: string}>,
    reviewContext: {
      ourSide: string[];
      theirSide: string[];
      sideAssignments?: Record<string, 'our_side' | 'their_side'>;
      optionalDetails?: string;
    }
  ): Promise<any> {
    try {
      const response = await this.axiosInstance.post('/word-addins/review-document', {
        text: text,
        paragraphs: paragraphs,
        review_context: reviewContext
      }, {
        timeout: 180000, // 3 minutes for full document review
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return {
        critical: [],
        warning: [],
        info: []
      };
    } catch (error: any) {
      console.error('Error reviewing document:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    const response = await this.axiosInstance.get('/auth/me', {
      timeout: 30000,
    });
    return response.data;
  }

  /**
   * Update the current user's Client Request source preferences.
   * Pass only the module(s) you want to change; omit to leave unchanged.
   */
  async updateClientRequestPreferences(
    moduleA?: string[],
    moduleB?: string[],
  ): Promise<ApiResponse<any>> {
    const body: { module_a?: string[]; module_b?: string[] } = {};
    if (moduleA !== undefined) body.module_a = moduleA;
    if (moduleB !== undefined) body.module_b = moduleB;
    const response = await this.axiosInstance.put('/auth/me/preferences', body, {
      timeout: 10000,
    });
    return response.data;
  }

  async updateCustomQuickResponses(labels: string[]): Promise<ApiResponse<any>> {
    const response = await this.axiosInstance.put(
      '/auth/me/preferences',
      { custom_quick_responses: labels },
      { timeout: 10000 },
    );
    return response.data;
  }

  /**
   * Update the current user's UI language preference.
   * Persisted under user_metadata.preferences.language and shared with the dashboard.
   */
  async updateLanguagePreference(language: string): Promise<ApiResponse<any>> {
    const response = await this.axiosInstance.put(
      '/auth/me/preferences',
      { language },
      { timeout: 10000 },
    );
    return response.data;
  }

  async listAgentSkills(language = 'en'): Promise<{
    success: boolean;
    data?: { skills: Array<{ id: string; name: string; description: string; category: string; icon: string }> };
    message?: string;
  }> {
    const response = await this.axiosInstance.get('/add-ins/agent-skills', {
      params: { language, scope: 'word-agent' },
      timeout: 30000,
    });
    return response.data;
  }

  async getAgentSkill(skillId: string, language = 'en'): Promise<{
    success: boolean;
    data?: { skill_id: string; metadata: Record<string, string>; content: string };
    message?: string;
  }> {
    const response = await this.axiosInstance.get(`/add-ins/agent-skills/${skillId}`, {
      params: { language },
      timeout: 30000,
    });
    return response.data;
  }

  async analyzeAgentSkill(params: {
    skillId: string;
    pastedText: string;
    paragraphs: Array<{ index: number; text: string }>;
    language?: 'he' | 'en';
  }): Promise<{
    success: boolean;
    data?: {
      client_requests: Array<{ text: string; clause_number: string }>;
      location_options: Array<{ label: string; paragraph_index: number }>;
      has_clause_numbers: boolean;
      ordering_context?: {
        pasted_generic_numbering: boolean;
        document_has_outer_clause_refs: boolean;
        outer_clause_refs: Array<{ clause_number: string; paragraph_index: number; preview?: string }>;
        needs_ordering_clarification: boolean;
        clarification_message: string;
      };
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/analyze',
        {
          skill_id: params.skillId,
          pasted_text: params.pastedText,
          paragraphs: params.paragraphs,
          language: params.language ?? 'he',
        },
        { timeout: 120000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill analysis failed');
    }
  }

  async executeAgentSkill(params: {
    skillId: string;
    clientRequests: Array<{ text: string; clause_number: string }>;
    paragraphs: Array<{ index: number; text: string }>;
    locationParagraphIndex: number;
    chronological: boolean;
    formatMode: 'plain' | 'track_changes';
    author?: string;
    language?: 'he' | 'en';
    orderMode?: 'pasted' | 'generic_numbered' | 'outer_clause' | 'append_at_location' | 'auto';
    outerClauseRefs?: Array<{ clause_number: string; paragraph_index: number; preview?: string }>;
    locationLabel?: string;
    locationOptions?: Array<{ label: string; paragraph_index: number }>;
  }): Promise<{
    success: boolean;
    data?: {
      insertions: Array<{
        client_request_text: string;
        target_paragraph_index: number;
        insert_mode: string;
        format: string;
        author?: string;
        paragraph_role?: string;
      }>;
      summary: string;
      insertion_strategy?: 'chained' | 'independent' | 'mixed';
      pending_placement?: {
        items: Array<{ index: number; text: string; text_preview: string }>;
        available_headings: Array<{ label: string; paragraph_index: number; anchor_text: string }>;
      } | null;
      strategy_trace?: string[];
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/execute',
        {
          skill_id: params.skillId,
          client_requests: params.clientRequests,
          paragraphs: params.paragraphs,
          location_paragraph_index: params.locationParagraphIndex,
          chronological: params.chronological,
          format_mode: params.formatMode,
          author: params.author ?? 'Unknown',
          language: params.language ?? 'he',
          order_mode: params.orderMode ?? 'pasted',
          outer_clause_refs: params.outerClauseRefs ?? null,
          location_label: params.locationLabel ?? '',
          location_options: (params.locationOptions ?? []).map((o) => ({
            label: o.label,
            paragraph_index: o.paragraph_index,
          })),
        },
        { timeout: 120000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill execution failed');
    }
  }

  /** Post-execute self-review follow-up: interpret a free-text "move X to Y"
   * instruction against the requests that were just installed. */
  async repositionAgentSkill(params: {
    skillId: string;
    instruction: string;
    recentInsertions: Array<{
      client_request_text: string;
      target_paragraph_index: number;
      format: string;
      author?: string;
    }>;
    paragraphs: Array<{ index: number; text: string }>;
    language?: 'he' | 'en';
  }): Promise<{
    success: boolean;
    data?: {
      moves: Array<{
        match_text: string;
        target_paragraph_index: number;
        format: 'plain' | 'track_changes';
        author?: string | null;
      }>;
      clarification?: string;
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/reposition',
        {
          skill_id: params.skillId,
          instruction: params.instruction,
          recent_insertions: params.recentInsertions,
          paragraphs: params.paragraphs,
          language: params.language ?? 'he',
        },
        { timeout: 60000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill reposition failed');
    }
  }

  /** Automatic post-insertion verification — no lawyer instruction. The model
   * inspects the resulting document on its own right after auto-mode's own
   * (regex/parsing-based) chronological placement, and reports any of THIS
   * execute() call's own insertions it finds genuinely misplaced — e.g.
   * landed in the middle of an existing clause's own multi-paragraph
   * declaration, something regex cannot reliably distinguish from an
   * unrelated trailing paragraph. Returns `moves` in the exact same shape
   * `repositionAgentSkill` does, so the caller applies them via the same
   * `repositionSkillInsertions` mechanism. */
  async selfCheckAgentSkillPlacement(params: {
    skillId: string;
    recentInsertions: Array<{
      client_request_text: string;
      target_paragraph_index: number;
      format: string;
      author?: string;
    }>;
    paragraphs: Array<{ index: number; text: string }>;
    language?: 'he' | 'en';
  }): Promise<{
    success: boolean;
    data?: {
      moves: Array<{
        match_text: string;
        target_paragraph_index: number;
        format: 'plain' | 'track_changes';
        author?: string | null;
        reason?: string;
      }>;
      all_correct?: boolean;
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/self-check-placement',
        {
          skill_id: params.skillId,
          recent_insertions: params.recentInsertions,
          paragraphs: params.paragraphs,
          language: params.language ?? 'he',
        },
        { timeout: 60000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill placement self-check failed');
    }
  }

  /** Second half of the ask-heading-first flow: the lawyer named a heading for
   * requests execute() held back (pending_placement) — resolve exact placement. */
  async placePendingAgentSkill(params: {
    skillId: string;
    pendingItems: Array<{ index: number; text: string }>;
    headingParagraphIndex: number;
    headingLabel: string;
    anchorText?: string;
    paragraphs: Array<{ index: number; text: string }>;
    formatMode: 'plain' | 'track_changes';
    author?: string;
    language?: 'he' | 'en';
  }): Promise<{
    success: boolean;
    data?: {
      insertions: Array<{
        client_request_text: string;
        target_paragraph_index: number;
        insert_mode: string;
        format: string;
        author?: string;
        paragraph_role?: string;
      }>;
      summary: string;
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/place-pending',
        {
          skill_id: params.skillId,
          pending_items: params.pendingItems,
          heading_paragraph_index: params.headingParagraphIndex,
          heading_label: params.headingLabel,
          anchor_text: params.anchorText ?? null,
          paragraphs: params.paragraphs,
          format_mode: params.formatMode,
          author: params.author ?? 'Unknown',
          language: params.language ?? 'he',
        },
        { timeout: 120000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill pending placement failed');
    }
  }

  /** Upfront confirmation step for auto mode: propose every appendix/agreement
   * citation group found in the pasted text, each with its own grounded
   * rationale, so the client can walk the lawyer through one wizard question
   * per group BEFORE anything is inserted. `correction` re-runs it with the
   * lawyer's free-text fix applied as a hard constraint. */
  async proposeAutoSkillStrategy(params: {
    skillId: string;
    clientRequests: Array<{ text: string; clause_number: string }>;
    paragraphs: Array<{ index: number; text: string }>;
    locationOptions: Array<{ label: string; paragraph_index: number }>;
    correction?: string;
    language?: 'he' | 'en';
  }): Promise<{
    success: boolean;
    data?: {
      groups: Array<{
        key: string;
        signal: string;
        count: number;
        request_indices: number[];
        suggested_paragraph_index: number | null;
        suggested_heading_label: string;
        rationale: string;
        text_previews: string[];
      }>;
      available_headings: Array<{ label: string; paragraph_index: number; anchor_text: string }>;
      strategy_trace?: string[];
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/propose-strategy',
        {
          skill_id: params.skillId,
          client_requests: params.clientRequests,
          paragraphs: params.paragraphs,
          location_options: params.locationOptions,
          correction: params.correction ?? null,
          language: params.language ?? 'he',
        },
        { timeout: 180000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill strategy proposal failed');
    }
  }

  /** Final step once the lawyer answered every wizard question: build the
   * insertion plan from the already-agreed per-group assignments (existing
   * heading, or a brand-new heading typed into the wizard's free-text option),
   * no more LLM calls needed for the mapping itself. */
  async executeConfirmedAgentSkill(params: {
    skillId: string;
    clientRequests: Array<{ text: string; clause_number: string }>;
    groupAssignments: Array<{
      request_indices: number[];
      target_paragraph_index?: number;
      anchor_text?: string;
      new_heading_text?: string;
    }>;
    availableHeadings: Array<{ label: string; paragraph_index: number; anchor_text: string }>;
    paragraphs: Array<{ index: number; text: string }>;
    formatMode: 'plain' | 'track_changes';
    author?: string;
    language?: 'he' | 'en';
  }): Promise<{
    success: boolean;
    data?: {
      insertions: Array<{
        client_request_text: string;
        target_paragraph_index: number;
        insert_mode: string;
        format: string;
        author?: string;
        paragraph_role?: string;
      }>;
      summary: string;
    };
    message?: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/add-ins/agent-skill/execute-confirmed',
        {
          skill_id: params.skillId,
          client_requests: params.clientRequests,
          group_assignments: params.groupAssignments,
          available_headings: params.availableHeadings,
          paragraphs: params.paragraphs,
          format_mode: params.formatMode,
          author: params.author ?? 'Unknown',
          language: params.language ?? 'he',
        },
        { timeout: 120000 },
      );
      return response.data;
    } catch (error) {
      throw this.formatAxiosError(error, 'Skill confirmed execution failed');
    }
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

